import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
    createChart,
    ColorType,
    LineStyle,
    IChartApi,
    ISeriesApi,
    Time,
} from 'lightweight-charts';
import { formatDecimal, formatInt } from '../../utils/numberFormat';
import {
    bucketSamples,
    PNL_INTERVAL_SECONDS,
    type PnlInterval,
    type PnlSample,
} from '../../hooks/usePnlHistory';
import { spreadPercent, type NotionalHistoryPoint } from '../../utils/pairSpreadHistory';

/**
 * The pair screen's spread chart, in the candlestick slot with the same theme. ONE
 * chart with two stacked regions (lightweight-charts 4 has no panes, so the regions
 * are two price scales with disjoint margins):
 *
 *   top    — the two legs as NOTIONAL in VND for ONE contract: futures price ×
 *            multiplier, and a basket whose share counts are fixed at the previous
 *            session's close (see `spreadBasket`). At that close the two are equal,
 *            so the lines start each session together and drift apart with their
 *            own legs. Notional and not raw price because 1,970 points and 60
 *            thousand VND cannot share one honest axis.
 *
 *   bottom — on the left axis, the spread those two lines make: futures ÷ basket − 1, in percent.
 *            0 = the previous close; above 0 the futures leg has got rich against
 *            the basket. Derived point by point from the lines above, so the two
 *            regions cannot disagree.
 *
 * Leg hues are blue (futures) and yellow (equity), never green or red, which
 * already mean profit and loss on this screen. The spread's sign is not a profit
 * either, so it gets violet/grey.
 */

const DERIV_HUE = '#3987e5';
const EQUITY_HUE = '#c98500';
const GRID = '#2B3139';
const AXIS_TEXT = '#848E9C';
const SPREAD_LINE = '#9085e9';
const NEUTRAL = '#5E6673';

interface PairPnlChartProps {
    samples: PnlSample[];
    /** Recent sessions' leg notionals rebuilt from candles, drawn ahead of the live samples. */
    notionalHistory?: NotionalHistoryPoint[];
    /**
     * The values at THIS tick, not at the last 5s sample — the header numbers
     * track the websocket directly while the lines advance in 5s steps.
     */
    live: {
        futValue: number | null;
        equityValue: number | null;
    };
    /** Bucket both panes onto this interval. */
    interval: PnlInterval;
    /** 0 ⇒ fill the parent, matching CandlestickChart. */
    height?: number;
    headerExtra?: React.ReactNode;
}

const Wrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => ($height > 0 ? `${$height}px` : '100%')};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 4px;
  padding: 12px;

  @media (max-width: 991px) {
    padding: 8px;
  }
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  flex-shrink: 0;
  gap: 12px;

  /* Title, legend chips and the interval/view controls do not fit one 360px row; let them
     wrap instead of crushing each other. */
  @media (max-width: 991px) {
    flex-wrap: wrap;
    gap: 6px 10px;
  }
`;

const Title = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  margin: 0;
  line-height: 1;
  white-space: nowrap;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;

  @media (max-width: 991px) {
    gap: 4px 10px;
  }
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 0;
`;

const Chip = styled.span<{ $color: string; $strong?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  font-weight: ${({ $strong }) => ($strong ? 700 : 400)};
  color: ${({ theme, $strong }) => ($strong ? theme.colors.text.primary : theme.colors.text.secondary)};

  &::before {
    content: '';
    width: 8px;
    height: ${({ $strong }) => ($strong ? '3px' : '8px')};
    border-radius: 2px;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
  }
`;

const Value = styled.span<{ $sign: number }>`
  font-family: 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  color: ${({ theme, $sign }) =>
        $sign > 0
            ? theme.colors.trading.buy
            : $sign < 0
                ? theme.colors.trading.sell
                : theme.colors.text.secondary};
`;

const PaneLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.tertiary};
  padding: 2px 0 1px;

  /* The trailing metadata wraps to several uppercase lines at phone width and eats the
     chart; keep the descriptor on one line and drop the rest. */
  @media (max-width: 991px) {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: 0.02em;

    > span + span {
      display: none;
    }
  }
`;

const ChartBox = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

/** Caption of the spread region, laid over the chart where that region starts. */
const RegionLabel = styled(PaneLabel)<{ $top: number; $left: number; $right: number }>`
  position: absolute;
  top: ${({ $top }) => `${$top * 100}%`};
  /* Inside the plot area: the axes on either side keep their own column. */
  left: ${({ $left }) => `${$left}px`};
  right: ${({ $right }) => `${$right}px`};
  padding-left: 6px;
  z-index: 2;
  pointer-events: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  pointer-events: none;
`;

const vndAxis = (v: number) =>
    Math.abs(v) >= 1_000_000 ? `${formatDecimal(v / 1_000_000, 2)}M` : formatInt(v);

/** UTC+7 offset in seconds — the market's clock, not the browser's. */
const VN_OFFSET_SECONDS = 7 * 3600;

/**
 * How far back the price pane reaches at each interval.
 *
 * The interval picks the candle scale for the history fetch (see CANDLE_SCALE in
 * usePairSpreadHistory), and one request per leg buys a fixed number of bars — so
 * finer bars mean less history. Shown on the pane because that trade is the whole
 * reason to touch the control, and it is invisible otherwise.
 */
const HISTORY_DEPTH: Record<PnlInterval, string> = {
    '5s': '~1 session',
    '1min': '~1 session',
    '5min': '~6 sessions',
    '1h': '~60 sessions',
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** `HH:MM` in VN time for a true-UTC epoch. Axis labels only; data is untouched. */
const vnClock = (epochSeconds: number): string => {
    const d = new Date((epochSeconds + VN_OFFSET_SECONDS) * 1000);
    return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
};

/** `DD/MM` in VN time — for the ticks that open a new day. */
const vnDate = (epochSeconds: number): string => {
    const d = new Date((epochSeconds + VN_OFFSET_SECONDS) * 1000);
    return `${pad2(d.getUTCDate())}/${pad2(d.getUTCMonth() + 1)}`;
};

/** VN calendar day of a true-UTC epoch, as a day number. */
const vnDayOf = (epochSeconds: number): number =>
    Math.floor((epochSeconds + VN_OFFSET_SECONDS) / 86400);

interface LegPoint {
    time: number;
    fut: number;
    eq: number;
}

/**
 * History first, live buffer after it.
 *
 * Both halves are the same arithmetic on the same clock, so the join is a plain
 * concatenation — except the buffer starts the moment the page opened, which is
 * inside the history's range, so whatever the buffer already covers is dropped
 * rather than drawn twice.
 */
function joinHistory(history: LegPoint[], live: LegPoint[]): LegPoint[] {
    const firstLive = live.length ? live[0].time : Infinity;
    return [...history.filter((p) => p.time < firstLive), ...live];
}

/**
 * Down-sample onto the pane's interval, keeping the LAST point of each bucket —
 * the same rule (and the same reason) as `bucketSamples`: these are levels, and
 * averaging a level invents a value the market never printed.
 */
function bucketPoints(points: LegPoint[], intervalSec: number): LegPoint[] {
    if (intervalSec <= 0 || points.length === 0) return points;
    const out: LegPoint[] = [];
    let bucket = -1;
    for (const p of points) {
        const b = Math.floor(p.time / intervalSec);
        if (b === bucket) out[out.length - 1] = p;
        else {
            out.push(p);
            bucket = b;
        }
    }
    return out;
}

const pctAxis = (v: number) => `${v > 0 ? '+' : ''}${formatDecimal(v, 2)}%`;

/**
 * One chart, two stacked regions: the legs on the right axis in the top part, the
 * spread on the left axis below it. Fractions of the chart height.
 */
const LEGS_MARGINS = { top: 0.04, bottom: 0.42 };
const SPREAD_MARGINS = { top: 0.66, bottom: 0.04 };
/** Where the spread caption sits — just above the spread region. */
const SPREAD_LABEL_TOP = 0.6;

const chartOptions = (width: number, height: number) => ({
    layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: AXIS_TEXT,
        fontSize: 11,
    },
    grid: {
        vertLines: { color: GRID, style: LineStyle.Dotted, visible: true },
        horzLines: { color: GRID, style: LineStyle.Dotted, visible: true },
    },
    width,
    height,
    autoSize: false,
    handleScale: { axisPressedMouseMove: false },
    timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: GRID,
        borderVisible: true,
        rightOffset: 2,
        // Series carry true UTC epochs; the library renders UTC, so without this
        // a 13:45 session reads "06:45". Day-boundary marks get the date, since the
        // chart can span several sessions.
        tickMarkFormatter: (time: number, tickMarkType: number) =>
            tickMarkType < 3 ? vnDate(time) : vnClock(time),
    },
    rightPriceScale: { borderColor: GRID, borderVisible: true, scaleMargins: LEGS_MARGINS },
    leftPriceScale: { visible: true, borderColor: GRID, borderVisible: true, scaleMargins: SPREAD_MARGINS },
    localization: {
        timeFormatter: (time: number) => vnClock(time),
    },
    crosshair: {
        vertLine: { color: AXIS_TEXT, style: LineStyle.Dotted, labelBackgroundColor: '#2B3139' },
        horzLine: { color: AXIS_TEXT, style: LineStyle.Dotted, labelBackgroundColor: '#2B3139' },
    },
});

/**
 * How far the view may sit from the newest bar and still count as "at the tail".
 *
 * Not zero: the resting position is `rightOffset` bars past the last point, and a drag that
 * ends a fraction of a bar short should still read as "following", not as a deliberate trip
 * into history. Two bars is small enough that a real pan away never passes for it.
 */
const TAIL_SLACK_BARS = 2;

const PairPnlChart: React.FC<PairPnlChartProps> = ({
    samples,
    notionalHistory,
    live,
    interval,
    height = 0,
    headerExtra,
}) => {
    const boxRef = useRef<HTMLDivElement>(null);
    const chart = useRef<IChartApi | null>(null);
    const futLine = useRef<ISeriesApi<'Line'> | null>(null);
    const eqLine = useRef<ISeriesApi<'Line'> | null>(null);
    const spreadSeries = useRef<ISeriesApi<'Baseline'> | null>(null);
    // Has the axis been framed once? Only the FIRST framing picks the window; after that the
    // user's zoom is theirs to keep.
    const framedOnce = useRef(false);
    // Bounds of the default view, refreshed with the data (see `frame`).
    const lastPointTime = useRef<number | null>(null);
    const firstTimeOnLatestDay = useRef<number | null>(null);

    /**
     * Is the view still parked at the newest point?
     *
     * `scrollPosition` is bars from the right edge of the DATA to the right edge of the
     * VIEW: zero at the tail, negative once the user has dragged back into history.
     */
    const atLatest = () => {
        const scale = chart.current?.timeScale();
        if (!scale) return true;
        const pos = scale.scrollPosition();
        return typeof pos === 'number' ? pos > -TAIL_SLACK_BARS : true;
    };

    /**
     * Keep the newest point in view across a refresh, without taking the axis away:
     * frame once, leave a view dragged into history alone, and otherwise follow the
     * tail at whatever zoom the user chose.
     */
    const follow = () => {
        const scale = chart.current?.timeScale();
        if (!scale) return;
        if (!framedOnce.current) {
            frame();
            return;
        }
        if (!atLatest()) return;
        scale.scrollToRealTime();
    };

    const frame = () => {
        const scale = chart.current?.timeScale();
        if (!scale) return;
        framedOnce.current = true;

        // Open on TODAY even when several sessions are loaded; older days stay one
        // drag to the left.
        const latest = lastPointTime.current;
        const todayStart = firstTimeOnLatestDay.current;
        if (latest != null && todayStart != null && todayStart < latest) {
            scale.setVisibleRange({ from: todayStart as Time, to: latest as Time });
            return;
        }
        scale.fitContent();
    };
    const [size, setSize] = useState({ w: 0, h: 0 });
    // Width of each price axis, so the spread caption sits between them.
    const [axisInset, setAxisInset] = useState({ left: 0, right: 0 });
    // Axis widths follow the labels, which only settle after the library draws.
    const measureAxes = () => {
        requestAnimationFrame(() => {
            const c = chart.current;
            if (!c) return;
            const left = c.priceScale('left').width();
            const right = c.priceScale('right').width();
            setAxisInset((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
        });
    };

    // ── size tracking (explicit sizes, no autoSize, so the chart cannot fight the
    //    flex parent — same approach as CandlestickChart) ─────────────────────
    useEffect(() => {
        const measure = () => {
            const el = boxRef.current;
            if (!el) return;
            // Only on a real change: a fresh object every measurement re-runs the
            // effect that follows the tail.
            setSize((prev) =>
                prev.w === el.clientWidth && prev.h === el.clientHeight
                    ? prev
                    : { w: el.clientWidth, h: el.clientHeight },
            );
        };
        measure();
        const ro = new ResizeObserver(measure);
        if (boxRef.current) ro.observe(boxRef.current);
        return () => ro.disconnect();
    }, []);

    // ── create / destroy the chart once ────────────────────────────────────
    useEffect(() => {
        if (!boxRef.current) return;
        const created = createChart(boxRef.current, chartOptions(1, 1));
        chart.current = created;

        const vndFormat = { type: 'custom' as const, formatter: vndAxis, minMove: 1 };
        futLine.current = created.addLineSeries({
            color: DERIV_HUE, lineWidth: 2, priceLineVisible: false, title: 'Futures',
            priceScaleId: 'right', priceFormat: vndFormat,
        });
        eqLine.current = created.addLineSeries({
            color: EQUITY_HUE, lineWidth: 2, priceLineVisible: false, title: 'Equity',
            priceScaleId: 'right', priceFormat: vndFormat,
        });

        spreadSeries.current = created.addBaselineSeries({
            priceScaleId: 'left',
            priceFormat: { type: 'custom', formatter: pctAxis, minMove: 0.0001 },
            baseValue: { type: 'price', price: 0 },
            topLineColor: SPREAD_LINE,
            topFillColor1: 'rgba(144, 133, 233, 0.20)',
            topFillColor2: 'rgba(144, 133, 233, 0.02)',
            bottomLineColor: NEUTRAL,
            bottomFillColor1: 'rgba(94, 102, 115, 0.02)',
            bottomFillColor2: 'rgba(94, 102, 115, 0.10)',
            lineWidth: 2,
            priceLineVisible: false,
            title: 'Spread',
        });
        spreadSeries.current.createPriceLine({
            price: 0,
            color: AXIS_TEXT,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            axisLabelVisible: true,
            title: 'prev close',
        });

        // A gesture means the initial framing has served its purpose: from here the window is
        // the user's, and `follow` only shifts it while they are parked at the tail.
        const el = boxRef.current;
        const claim = () => { framedOnce.current = true; };
        const CLAIM_EVENTS = ['pointerdown', 'mousedown', 'touchstart', 'wheel'] as const;
        CLAIM_EVENTS.forEach((type) => el.addEventListener(type, claim, { passive: true }));

        return () => {
            CLAIM_EVENTS.forEach((type) => el.removeEventListener(type, claim));
            futLine.current = null;
            eqLine.current = null;
            spreadSeries.current = null;
            created.remove();
            chart.current = null;
        };
    }, []);

    // ── apply measured size ────────────────────────────────────────────────
    useEffect(() => {
        if (chart.current && size.w > 0) {
            chart.current.applyOptions({ width: size.w, height: Math.max(size.h, 80) });
        }
        // `follow` and `measureAxes` read refs only, so they are stable enough to leave out.
        follow();
        measureAxes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size]);

    // A new bucket size is a new framing question: the old window means nothing at a
    // different resolution, so frame it again from scratch.
    useEffect(() => {
        framedOnce.current = false;
    }, [interval]);

    // ── data ───────────────────────────────────────────────────────────────
    // Every point carries BOTH legs, so the spread below is computed from exactly
    // the two values drawn above it at that time.
    const [spreadPoints, setSpreadPoints] = useState(0);
    useEffect(() => {
        if (!chart.current) return;
        const intervalSec = PNL_INTERVAL_SECONDS[interval];

        const livePoints = bucketSamples(samples, intervalSec)
            .filter((s) => s.futValue != null && s.equityValue != null)
            .map((s) => ({ time: s.time, fut: s.futValue as number, eq: s.equityValue as number }));
        const historyPoints = (notionalHistory ?? [])
            .map((p) => ({ time: p.time, fut: p.futValue, eq: p.equityValue }));
        const points = bucketPoints(joinHistory(historyPoints, livePoints), intervalSec);

        futLine.current?.setData(points.map((p) => ({ time: p.time as Time, value: p.fut })));
        eqLine.current?.setData(points.map((p) => ({ time: p.time as Time, value: p.eq })));
        const spread = points
            .map((p) => ({ time: p.time as Time, value: spreadPercent(p.fut, p.eq) }))
            .filter((p): p is { time: Time; value: number } => p.value != null);
        spreadSeries.current?.setData(spread);
        setSpreadPoints(spread.length);
        measureAxes();

        // Default view bounds: the latest VN day present.
        if (points.length) {
            const last = points[points.length - 1].time;
            const day = vnDayOf(last);
            lastPointTime.current = last;
            firstTimeOnLatestDay.current = points.find((p) => vnDayOf(p.time) === day)?.time ?? null;
        } else {
            lastPointTime.current = null;
            firstTimeOnLatestDay.current = null;
        }
    }, [samples, notionalHistory, interval]);

    /**
     * Framing is NOT part of the data effect, which runs every 5 s with the live
     * buffer — re-framing there would yank a pan back on that clock. Frame only
     * when the SHAPE of the data changes: a new interval, or the history arriving.
     */
    const hasAnyData = samples.length > 0 || (notionalHistory?.length ?? 0) > 0;

    useEffect(() => {
        follow();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAnyData, notionalHistory, interval]);

    const spreadPct = spreadPercent(live.futValue, live.equityValue);
    const spreadVnd =
        live.futValue != null && live.equityValue != null ? live.futValue - live.equityValue : null;

    return (
        <Wrapper $height={height}>
            <Header>
                <Title>Pair</Title>
                <Legend>
                    <Chip $color={DERIV_HUE}>
                        Futures
                        <Value $sign={0}>
                            {live.futValue != null ? vndAxis(live.futValue) : 'no quote'}
                        </Value>
                    </Chip>
                    <Chip $color={EQUITY_HUE}>
                        Equity
                        <Value $sign={0}>
                            {live.equityValue != null ? vndAxis(live.equityValue) : 'no close'}
                        </Value>
                    </Chip>
                    {spreadPct != null && spreadVnd != null && (
                        <Chip $color={SPREAD_LINE}>
                            Spread
                            <Value $sign={0}>{pctAxis(spreadPct)}</Value>
                            <Value $sign={0} style={{ opacity: 0.75 }}>
                                {spreadVnd > 0 ? '+' : ''}{vndAxis(spreadVnd)}
                            </Value>
                        </Chip>
                    )}
                </Legend>
                {headerExtra}
            </Header>

            <PaneLabel>
                <span>Legs · ₫ notional (right axis) · 1 contract vs basket fixed at previous close</span>
                <span>{`${interval} · ${HISTORY_DEPTH[interval]}`}</span>
            </PaneLabel>
            <ChartBox ref={boxRef}>
                <RegionLabel $top={SPREAD_LABEL_TOP} $left={axisInset.left} $right={axisInset.right}>
                    <span>Spread % (left axis) · futures ÷ basket − 1 · 0 = previous close (ATC)</span>
                </RegionLabel>
                {live.futValue == null && live.equityValue == null && spreadPoints === 0 && (
                    <Overlay>No quote on either leg yet — pick a contract and a basket.</Overlay>
                )}
                {live.futValue != null && live.equityValue == null && spreadPoints === 0 && (
                    <Overlay>
                        Futures only: the basket has no previous close yet — no symbol
                        selected, no weight, or no candle for one of them.
                    </Overlay>
                )}
            </ChartBox>
        </Wrapper>
    );
};

export default PairPnlChart;
